'use client';

import { Button, Icon, type IconName } from '@/components/ui';
import { cn } from '@/libs';
import {
  CommunicationBannerType,
  type CommunicationBanner as CommunicationBannerData,
} from '@/types/models/CommunicationBanner';

import { useCommunicationBanner } from './hooks';

type CommunicationBannerProps = {
  banner: CommunicationBannerData | null;
  className?: string;
};

type BannerStyle = {
  container: string;
  iconWrapper: string;
  icon: IconName;
  ctaVariant: 'primary' | 'secondary' | 'tertiary';
};

const bannerStyles: Record<CommunicationBannerType, BannerStyle> = {
  [CommunicationBannerType.ALERT]: {
    container: 'border-error/30 bg-error/10 text-foreground',
    iconWrapper: 'bg-error/15 text-error',
    icon: 'Info',
    ctaVariant: 'primary',
  },
  [CommunicationBannerType.INFO]: {
    container: 'border-primary/25 bg-primary/10 text-foreground',
    iconWrapper: 'bg-primary/15 text-primary',
    icon: 'Info',
    ctaVariant: 'primary',
  },
  [CommunicationBannerType.SALE]: {
    container: 'border-secondary/35 bg-secondary/15 text-foreground',
    iconWrapper: 'bg-secondary/20 text-secondary',
    icon: 'Tag',
    ctaVariant: 'secondary',
  },
};

const CommunicationBannerCard = ({
  banner,
  className,
}: {
  banner: CommunicationBannerData;
  className?: string;
}) => {
  const { hasCta, isExternalCta, isVisible, handleDismiss } = useCommunicationBanner({
    banner,
  });
  const styles = bannerStyles[banner.type];
  const IconComponent = Icon[styles.icon];

  if (!isVisible) {
    return null;
  }

  return (
    <section
      data-testid="communication-banner"
      className={cn(
        'relative flex w-full items-start gap-3 rounded-lg border p-3 shadow-sm md:p-4',
        styles.container,
        className,
      )}
    >
      <div
        className={cn(
          'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md',
          styles.iconWrapper,
        )}
        aria-hidden="true"
      >
        <IconComponent width={20} height={20} />
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-2 pr-8">
        <div className="flex flex-col gap-1">
          <h2 className="text-sm font-semibold leading-snug md:text-base">
            {banner.title}
          </h2>
          <p className="whitespace-pre-line text-sm leading-relaxed text-foreground/80">
            {banner.copy}
          </p>
        </div>

        {hasCta && (
          <Button
            as="a"
            href={banner.ctaUrl ?? undefined}
            target={isExternalCta ? '_blank' : undefined}
            rel={isExternalCta ? 'noopener noreferrer' : undefined}
            variant={styles.ctaVariant}
            size="small"
            className="w-fit px-3"
          >
            {banner.ctaLabel}
          </Button>
        )}
      </div>

      {banner.dismissable && (
        <button
          type="button"
          aria-label="Fechar comunicado"
          className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-md text-foreground/70 transition-colors hover:bg-foreground/10 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground"
          onClick={handleDismiss}
        >
          <Icon.Close width={16} height={16} />
        </button>
      )}
    </section>
  );
};

export const CommunicationBanner = ({ banner, className }: CommunicationBannerProps) => {
  if (!banner) {
    return null;
  }

  return <CommunicationBannerCard banner={banner} className={className} />;
};
